import { ChangeEvent, useEffect, useState } from "react";
import { Company } from "../models/Companies/Company";
import { User } from "../models/Users/User";
import agent from "../../api/agent";
import { CompanyCreate } from "../models/Companies/CompanyCreate";
import { UserCreate } from "../models/Users/UserCreate";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import {
  Stack,
  Typography,
  Tooltip,
  Fab,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
  Box,
  TextField,
  TablePagination,
  Divider,
  CircularProgress,
} from "@mui/material";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import CreateCompanyForm from "../forms/Companies/CreateCompanyForm";
import EditCompanyForm from "../forms/Companies/EditCompanyForm";
import CreateUserForm from "../forms/Users/CreateUserForm";
import EditUserForm from "../forms/Users/EditUserForm";
import { Link as RouterLink } from "react-router-dom";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";
import ResetPassword from "../models/Users/ResetPassword";
import PasswordResetForm from "../forms/Users/PasswordResetForm";
import LicenseByUser from "../details/LicenseByUser";

export default function CompanyDashboard() {
  const [loading, setLoading] = useState<boolean>(true);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [filteredCompanyList, setFilteredCompanyList] = useState<Company[]>([]);
  const [userList, setUserList] = useState<User[]>([]);
  const [expandedCompanyId, setExpandedCompanyId] = useState<number[]>([]);

  //For Company part
  const [createCompanyFormOpen, setCreateCompanyFormOpen] =
    useState<boolean>(false);
  const [editCompanyFormOpen, setEditCompanyFormOpen] =
    useState<boolean>(false);
  const [deleteCompanyDialogOpen, setDeleteCompanyDialogOpen] =
    useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyCreate | null>(
    null,
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(0);
  //   For create new user under company
  const [selectedCompanyIdForUser, setSelectedCompanyIdForUser] =
    useState<number>(0);

  // For Search function(Store the user input)
  const [searchTerm, setSearchTerm] = useState("");

  //For User part
  const [createUserFormOpen, setCreateUserFormOpen] = useState<boolean>(false);
  const [editUserFormOpen, setEditUserFormOpen] = useState<boolean>(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] =
    useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserCreate | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);

  // Pagination (Company and User)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [pageForUser, setPageForUser] = useState(0);
  const [rowsPerPageForUser, setRowsPerPageForUser] = useState(50);

  //For User password reset
  const [pwResetFormOpenState, setPwResetFormOpenState] =
    useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<string>("");

  // Authentication
  const [token, setToken] = useState<string>("");

  // Show License Details by User
  const [licenseDetailsFormOpenState, setLicenseDetailsFormOpenState] =
    useState<boolean>(false);
  const [selectedUserIdForLicense, setSelectedUserIdForLicense] = useState<
    number | undefined
  >(undefined);

  // For export to CSV
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          setToken(tokenFLocal);
          // get the data(Company list)
          const companyResponse = await agent.Company.list(tokenFLocal);
          setCompanyList(companyResponse);
          setFilteredCompanyList(companyResponse);

          // get the data(User List)
          const userResponse = await agent.User.list(tokenFLocal);
          setUserList(userResponse);

          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Function to filter the User based on the company id
  const filterUserByCompany = (companyId: number): User[] => {
    return userList.filter((user) => user.companyId === companyId);
  };

  // Function to handle expand/collapse
  const handleExpand = (companyId: number) => {
    setExpandedCompanyId(
      (prevIds) =>
        prevIds.includes(companyId)
          ? prevIds.filter((id) => id !== companyId) // Remove if already expanded
          : [...prevIds, companyId], // Add if not expanded
    );
  };

  // function to open create form(Company)
  function handleCompanyCreateFormOpen() {
    setCreateCompanyFormOpen(true);
  }

  // function to open edit form(Company)
  function handleCompanyEditFormOpen(companyId: number) {
    const getCompany = companyList.find((x) => x.companyId === companyId);
    if (getCompany) {
      // Map Company to CompanyCreate
      const CompanyCreate: CompanyCreate = {
        companyName: getCompany.companyName,
        companyDomain: getCompany.companyDomain,
        // Add other properties as needed
      };
      setSelectedCompany(CompanyCreate);
      setSelectedCompanyId(companyId);
      setEditCompanyFormOpen(true);
    }
  }

  // function for close the create and edit form
  function handleCompanyCreateOrEditFormClose() {
    setCreateCompanyFormOpen(false);
    setEditCompanyFormOpen(false);
    setSelectedCompany(null);
    setSelectedCompanyId(0);
  }

  // Function to create new Company
  async function handleCreateSubmit(formData: CompanyCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.Company.create(formData, token).then(async () => {
        const updatedCompanies = await agent.Company.list(token);
        setCompanyList(updatedCompanies);
        handleCompanyCreateOrEditFormClose();
        showToast("Company Created Successfully", "success");
      });
    } catch (error) {
      console.error("Error Create Company:", error);
      showToast("Error Create Company", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to edit Company
  async function handleEditSubmit(formData: CompanyCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.Company.update(selectedCompanyId, formData, token).then(
        async () => {
          const updatedCompanies = await agent.Company.list(token);
          setCompanyList(updatedCompanies);
          handleCompanyCreateOrEditFormClose();
          showToast("Company Edited Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error updating Company:", error);
      showToast("Error updating Company", "error");
    } finally {
      setLoading(false);
    }
  }

  function handleDeleteCompanyOnClick(companyId: number) {
    setSelectedCompanyId(companyId);
    setDeleteCompanyDialogOpen(true);
  }

  function handleDeleteCompanyDialogClose() {
    setSelectedCompanyId(0);
    setDeleteCompanyDialogOpen(false);
  }

  async function handleCompanyConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedCompanyId === 0) return;
    setLoading(true);
    try {
      await agent.Company.delete(selectedCompanyId, token).then(async () => {
        const updatedCompanies = await agent.Company.list(token);
        setCompanyList(updatedCompanies);
        showToast("Company Deleted Successfully", "success");
      });
    } catch (error) {
      console.error("Error deleting Company Details:", error);
      showToast("Error deleting Company Details", "error");
    } finally {
      setLoading(false);
      handleDeleteCompanyDialogClose(); //Reset all the delete form open state
    }
  }

  // Function for user Password Reset
  function handlePWResetFormOpen(email: string) {
    setSelectedEmail(email);
    setPwResetFormOpenState(true);
  }

  function handlePWResetFormClose() {
    setSelectedEmail("");
    setPwResetFormOpenState(false);
  }

  async function handlePWReset(formData: ResetPassword) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.resetPassword(selectedEmail, formData, token).then(
        () => {
          handlePWResetFormClose();
          showToast("User Password Reset Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error reset user Password:", error);
      showToast("Error Reset User Password", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function for View License Details by User
  function handleLicenseDetailsFormOpen(userId: number) {
    setSelectedUserIdForLicense(userId);
    setLicenseDetailsFormOpenState(true);
  }

  function handleLicenseDetailsFormClose() {
    setSelectedUserIdForLicense(undefined);
    setLicenseDetailsFormOpenState(false);
  }

  // Function to search and filter
  useEffect(() => {
    const filtered = companyList.filter((company) =>
      company.companyName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredCompanyList(filtered);
  }, [searchTerm, companyList]);

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  // Function for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ***************************USER PART***************************
  // Function to open create form(User)
  function handleUserCreateFormOpen(companyId: number) {
    setCreateUserFormOpen(true);
    setSelectedCompanyIdForUser(companyId);
  }

  // function to open edit form(User)
  function handleUserEditFormOpen(userId: number) {
    const getUser = userList.find((x) => x.userId === userId);
    if (getUser) {
      // Map User to UserCreate
      const userCreate: UserCreate = {
        email: getUser.email,
        userName: getUser.userName,
        phoneNumber: getUser.phoneNumber,
        role: getUser.role,
        companyId: getUser.companyId,
        // Add other properties as needed
      };
      setSelectedUser(userCreate);
      setSelectedUserId(userId);
      setEditUserFormOpen(true);
    }
  }

  // function for close the create and edit form
  function handleUserCreateOrEditFormClose() {
    setCreateUserFormOpen(false);
    setEditUserFormOpen(false);
    setSelectedUser(null);
    setSelectedUserId(0);
    setSelectedCompanyIdForUser(0);
  }

  // Function to create new User
  async function handleCreateSubmitForUser(formData: UserCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.create(formData, token).then(async () => {
        const updatedUser = await agent.User.list(token);
        setUserList(updatedUser);
        handleUserCreateOrEditFormClose();
        showToast("User Created Successfully", "success");
      });
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data;

        // Check for email already exists error
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Email already exists")
        ) {
          showToast("Email already exists", "error");
        }
        // Check for email domain validation error
        else if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Invalid Email domain")
        ) {
          showToast("Invalid email domain", "error");
        }
        // Check for general bad request with specific messages
        else if (typeof errorMessage === "string") {
          // Display whatever error message came from the backend
          showToast(errorMessage, "error");
        } else if (errorMessage.errors) {
          // Handle validation errors object (if backend returns this format)
          const firstError = Object.values(errorMessage.errors)[0] as string[];
          showToast(firstError?.[0] || "Validation error", "error");
        } else {
          // Fallback for other response formats
          showToast(errorMessage.message || "Failed to create user", "error");
        }
      } else {
        // Something else went wrong
        showToast("An unexpected error occurred", "error");
        console.error("Error setting up request:", error.message);
      }
    } finally {
      setLoading(false);
    }
  }

  // Function to edit User details
  async function handleEditSubmitForUser(formData: UserCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.update(selectedUserId, formData, token).then(
        async () => {
          const updatedUsers = await agent.User.list(token);
          setUserList(updatedUsers);
          handleUserCreateOrEditFormClose();
          showToast("User Edited Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error updating user:", error);
      showToast("Error updating user", "error");
    } finally {
      setLoading(false);
    }
  }

  function handleDeleteUserOnClick(userId: number) {
    setSelectedUserId(userId);
    setDeleteUserDialogOpen(true);
  }

  function handleDeleteUserDialogClose() {
    setSelectedUserId(0);
    setDeleteUserDialogOpen(false);
  }

  async function handleUserConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedUserId === 0) return;

    setLoading(true);
    try {
      if (checkTokenExpiration(token)) {
        return;
      }
      await agent.User.delete(selectedUserId, token).then(async () => {
        const updatedUser = await agent.User.list(token);
        setUserList(updatedUser);
        handleDeleteUserDialogClose();
        showToast("User Deleted Successfully", "success");
      });
    } catch (error) {
      console.error("Error deleting User:", error);
      showToast("Error deleting user", "error");
    } finally {
      setLoading(false);
      handleDeleteUserDialogClose(); //Reset all the delete form open state
    }
  }

  // Function for pagination
  const handleChangePageForUser = (event: unknown, newPage: number) => {
    setPageForUser(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPageForUser = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPageForUser(parseInt(event.target.value, 10));
    setPageForUser(0);
  };

  // Function to export Company list to CSV file
  const handleExport = async () => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setExportLoading(true);
    try {
      const response = await agent.Company.exportToCSV(token);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      let filename = `company-export-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Exported to CSV successfully", "success");
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      showToast("Error exporting to CSV", "error");
    } finally {
      setExportLoading(false);
    }
  };

  // trigger the loader, loading before the data is retrieved from database
  if (loading) return <LoadingComponent content="Loading Data" />;
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Company List</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Create New" followCursor>
            <Fab
              color="primary"
              onClick={() => handleCompanyCreateFormOpen()}
              size="small"
            >
              <AddIcon />
            </Fab>
          </Tooltip>

          <Tooltip title="Export to CSV" followCursor>
            <span>
              <Fab
                color="success"
                onClick={() => handleExport()}
                size="small"
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <GetAppIcon />
                )}
              </Fab>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Box>
        <TextField
          label="Search"
          // margin="normal"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </Box>
      {filteredCompanyList.length === 0 ? (
        <Box sx={{ mt: 5 }}>
          <Divider>
            <Typography variant="button">No Record found</Typography>
          </Divider>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ width: "50px" }}
                ></TableCell>
                <TableCell>Company Name</TableCell>
                <TableCell>Company Code</TableCell>
                <TableCell>Company Domain</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCompanyList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((company) => (
                  <>
                    <TableRow key={company.companyId}>
                      <TableCell padding="checkbox" sx={{ width: "50px" }}>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleExpand(company.companyId)}
                        >
                          {expandedCompanyId.includes(company.companyId) ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Company's Detail">
                          <Link
                            to={`/company/details/${company.companyId}`}
                            component={RouterLink}
                            underline="hover"
                          >
                            {company.companyName}
                          </Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{company.companyCode}</TableCell>
                      <TableCell>{company.companyDomain}</TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px", // Reduce padding (default is 16px)
                          width: "2%", // Minimize width to content
                          whiteSpace: "nowrap", // Prevent wrapping
                        }}
                      >
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit Company Detail">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() =>
                                handleCompanyEditFormOpen(company.companyId)
                              }
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Company">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() =>
                                handleDeleteCompanyOnClick(company.companyId)
                              }
                            >
                              <DeleteIcon sx={{ fontSize: 27 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ padding: 0, borderBottom: 0 }}
                      >
                        <Collapse
                          in={expandedCompanyId.includes(company.companyId)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 2, p: 2 }}
                          >
                            <Typography variant="h5">Users</Typography>
                            <Tooltip title="Create New" followCursor>
                              <Fab
                                color="primary"
                                onClick={() =>
                                  handleUserCreateFormOpen(company.companyId)
                                }
                                size="small"
                              >
                                <AddIcon />
                              </Fab>
                            </Tooltip>
                          </Stack>
                          {filterUserByCompany(company.companyId).length ===
                          0 ? (
                            <Divider>
                              <Typography variant="button">
                                NO USER LISTED UNDER THIS COMPANY YET
                              </Typography>
                            </Divider>
                          ) : (
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      padding="checkbox"
                                      sx={{ width: "50px" }}
                                    ></TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>UserName</TableCell>
                                    <TableCell>Phone Number</TableCell>
                                    <TableCell>Roles</TableCell>
                                    <TableCell sx={{ textAlign: "center" }}>
                                      Number of Licenses Assigned
                                    </TableCell>
                                    <TableCell>Action</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {filterUserByCompany(company.companyId)
                                    .slice(
                                      pageForUser * rowsPerPageForUser,
                                      pageForUser * rowsPerPageForUser +
                                        rowsPerPageForUser,
                                    )
                                    .map((user) => (
                                      <TableRow key={user.userId}>
                                        <TableCell
                                          padding="checkbox"
                                          sx={{ width: "50px" }}
                                        ></TableCell>

                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.userName}</TableCell>
                                        <TableCell>
                                          {user.phoneNumber}
                                        </TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                          {user.licenseCount}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            padding: "0 16px", // Reduce padding (default is 16px)
                                            width: "2%", // Minimize width to content
                                            whiteSpace: "nowrap", // Prevent wrapping
                                          }}
                                        >
                                          <Stack direction="row" spacing={1}>
                                            {/* Button for View License Details */}
                                            <Tooltip
                                              title={
                                                user.licenseCount === 0
                                                  ? "This user has no licenses"
                                                  : "View/Edit Licenses"
                                              }
                                            >
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    handleLicenseDetailsFormOpen(
                                                      user.userId,
                                                    )
                                                  }
                                                  disabled={
                                                    user.licenseCount === 0
                                                  }
                                                  sx={{
                                                    opacity:
                                                      user.licenseCount === 0
                                                        ? 0.5
                                                        : 1,
                                                  }}
                                                >
                                                  <VisibilityIcon />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                            {/* Button for password reset */}
                                            <Tooltip title="User Password Reset">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handlePWResetFormOpen(
                                                    user.email,
                                                  )
                                                }
                                              >
                                                <LockResetIcon />
                                              </IconButton>
                                            </Tooltip>
                                            <IconButton
                                              color="success"
                                              size="small"
                                              onClick={() =>
                                                handleUserEditFormOpen(
                                                  user.userId,
                                                )
                                              }
                                            >
                                              <EditIcon />
                                            </IconButton>
                                            <IconButton
                                              color="error"
                                              size="small"
                                              onClick={() =>
                                                handleDeleteUserOnClick(
                                                  user.userId,
                                                )
                                              }
                                            >
                                              <DeleteIcon
                                                sx={{ fontSize: 27 }}
                                              />
                                            </IconButton>
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                              {/* Pagination Component (User Table) */}
                              <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
                                component="div"
                                count={
                                  filterUserByCompany(company.companyId).length
                                } // Total number of rows
                                rowsPerPage={rowsPerPageForUser}
                                page={pageForUser}
                                onPageChange={handleChangePageForUser}
                                onRowsPerPageChange={
                                  handleChangeRowsPerPageForUser
                                }
                                sx={{
                                  ".MuiTablePagination-toolbar": {
                                    paddingLeft: 2, // Adjust padding as needed
                                  },
                                }}
                              />
                            </TableContainer>
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ))}
            </TableBody>
          </Table>
          {/* Pagination Component (Company Table) */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
            component="div"
            count={filteredCompanyList.length} // Total number of rows
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-toolbar": {
                paddingLeft: 2, // Adjust padding as needed
              },
            }}
          />
        </TableContainer>
      )}

      {/* --------------------- Company MODEL------------------------------- */}
      {/* Model for Company create form */}
      <Dialog
        open={createCompanyFormOpen}
        onClose={handleCompanyCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Create New Company
          </Typography>
        </DialogTitle>
        <DialogContent>
          <CreateCompanyForm
            closeForm={handleCompanyCreateOrEditFormClose}
            create={handleCreateSubmit}
          />
        </DialogContent>
      </Dialog>

      {/* Model for Company edit form */}
      <Dialog
        open={editCompanyFormOpen}
        onClose={handleCompanyCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Edit Company Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <EditCompanyForm
              selectedCompany={selectedCompany}
              closeForm={handleCompanyCreateOrEditFormClose}
              edit={handleEditSubmit}
            />
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmationBox
        open={deleteCompanyDialogOpen}
        onClose={handleDeleteCompanyDialogClose}
        onConfirm={handleCompanyConfirmDelete}
      />

      {/* --------------------- User MODEL------------------------------- */}
      {/* Model for user create form */}
      <Dialog
        open={createUserFormOpen}
        onClose={handleUserCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Create New User
          </Typography>
        </DialogTitle>
        <DialogContent>
          <CreateUserForm
            selectedCompanyId={selectedCompanyIdForUser}
            closeForm={handleUserCreateOrEditFormClose}
            create={handleCreateSubmitForUser}
            fromUserDashboard={false}
          />
        </DialogContent>
      </Dialog>

      {/* Model for user edit form */}
      <Dialog
        open={editUserFormOpen}
        onClose={handleUserCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Edit User Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <EditUserForm
              selectedUser={selectedUser}
              closeForm={handleUserCreateOrEditFormClose}
              edit={handleEditSubmitForUser}
              fromUserDashboard={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Model for user Password Reset form */}
      <Dialog
        open={pwResetFormOpenState}
        onClose={handlePWResetFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            User Password Reset
          </Typography>
        </DialogTitle>
        <DialogContent>
          <PasswordResetForm
            email={selectedEmail}
            closeForm={handlePWResetFormClose}
            passwordReset={handlePWReset}
          />
        </DialogContent>
      </Dialog>

      {/* Model for license details form */}
      <Dialog
        open={licenseDetailsFormOpenState}
        onClose={handleLicenseDetailsFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            ml: 2,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="h2">
            License Details
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleLicenseDetailsFormClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              "&:hover": {
                color: (theme) => theme.palette.grey[700],
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* {selectedUser && ( */}
          <LicenseByUser
            userIdForChecking={selectedUserIdForLicense}
            closeForm={handleLicenseDetailsFormClose}
          />
          {/* )} */}
        </DialogContent>
      </Dialog>

      <DeleteConfirmationBox
        open={deleteUserDialogOpen}
        onClose={handleDeleteUserDialogClose}
        onConfirm={handleUserConfirmDelete}
      />
    </>
  );
}
